import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SearchIcon } from '@common/utils/icons/icons';
import SelectInput from 'components/forms/SelectInput';

export interface FilterOption {
  id: string;
  label: string;
  caption?: string;
  group?: string;
}

const MENU_MAX_HEIGHT = 360;
const MAX_RENDERED_OPTIONS = 100;
const MAX_RENDERED_CHIPS = 3;
const SELECTED_SECTION_MIN_OPTIONS = 10;

const FilterMultiSelect: React.FC<{
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  loadingMessage?: string;
  groups?: { key: string, label: string }[];
}> = ({ options, value, onChange, disabled, loading = false, loadingMessage, groups }) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  const optionsById = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options],
  );

  const matches = useMemo(() => {
    const groupOrder = new Map<string | undefined, number>(
      (groups ?? []).map((group, index) => [group.key, index]),
    );

    const cleanedSearch = deferredSearch.trim().toLowerCase();
    const filteredOptions = cleanedSearch === ''
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(cleanedSearch));

    return [...filteredOptions].sort((a, b) => {
      const groupDiff = (groupOrder.get(a.group) ?? 0) - (groupOrder.get(b.group) ?? 0);
      return groupDiff !== 0 ? groupDiff : a.label.localeCompare(b.label);
    });
  }, [deferredSearch, options, groups]);

  const { visibleOptions, hiddenMatchCount } = useMemo(() => {
    const capped = matches.slice(0, MAX_RENDERED_OPTIONS);
    const cappedIds = new Set(capped.map((option) => option.id));
    const selectedBeyondTheCap = value
      .filter((id) => !cappedIds.has(id))
      .map((id) => optionsById.get(id))
      .filter((option): option is FilterOption => option !== undefined);

    return {
      visibleOptions: [...capped, ...selectedBeyondTheCap],
      hiddenMatchCount: matches.length - capped.length,
    };
  }, [matches, value, optionsById]);

  const selectedIds = useMemo(() => new Set(value), [value]);

  const selectedMatches = matches.filter((option) => selectedIds.has(option.id));

  const showSelectedSection = options.length > SELECTED_SECTION_MIN_OPTIONS && selectedMatches.length > 0;

  const changeHandler = useCallback(
    (event: { target: { value: unknown } }) => onChange(event.target.value as string[]),
    [onChange],
  );

  const closeHandler = useCallback(() => setSearch(''), []);

  const renderValue = useCallback(
    (selected: unknown): React.ReactNode => {
      if (loading) {
        return (
          <Stack direction="row" gap={1} alignItems="center" color="text.secondary">
            <CircularProgress size={14} />
            <Typography variant="body2">{loadingMessage ?? t('loading')}</Typography>
          </Stack>
        );
      }

      const ids = selected as string[];

      if (ids.length === 0) {
        return t('fleet-management-filter-option-all');
      }

      const shown = ids.slice(0, MAX_RENDERED_CHIPS);

      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {shown.map((id) => (
            <Chip key={id} size="small" label={optionsById.get(id)?.label ?? id} />
          ))}
          {ids.length > shown.length && (
            <Chip size="small" variant="outlined" label={`+${ids.length - shown.length}`} />
          )}
        </Stack>
      );
    },
    [loading, loadingMessage, optionsById, t],
  );

  const renderOption = useCallback((option: FilterOption, keyPrefix: string) => (
    <MenuItem key={`${keyPrefix}-${option.id}`} value={option.id} sx={{ gap: 1 }}>
      <Checkbox checked={selectedIds.has(option.id)} />
      <Stack alignItems="flex-start" width="100%" textAlign="left">
        {option.label}
        {option.caption && <Typography variant="caption" color="text.secondary">{option.caption}</Typography>}
      </Stack>
    </MenuItem>
  ), [selectedIds]);

  const renderedMenuItems = useMemo(() => {
    let previousGroupKey: string | undefined;

    return visibleOptions.flatMap((option) => {
      const itemsForOption = [];
      if ((groups?.length ?? 0) > 1 && option.group !== previousGroupKey) {
        const matchingGroup = groups?.find((group) => group.key === option.group);
        if (matchingGroup) {
          itemsForOption.push(
            <ListSubheader key={`group-${matchingGroup.key}`} sx={{ backgroundColor: 'background.paper' }}>
              {matchingGroup.label}
            </ListSubheader>,
          );
        }
        previousGroupKey = option.group;
      }
      itemsForOption.push(renderOption(option, 'all'));
      return itemsForOption;
    });
  }, [visibleOptions, groups, renderOption]);

  return (
    <SelectInput
      multiple
      displayEmpty
      disabled={disabled || loading}
      value={value}
      onChange={changeHandler}
      renderValue={renderValue}
      // The search box needs the focus the menu would otherwise hand to the first option.
      MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: MENU_MAX_HEIGHT } } }}
      onClose={closeHandler}
    >
      <ListSubheader sx={{ p: 1, lineHeight: 1, backgroundColor: 'background.paper' }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={search}
          placeholder={t('input-label-search')}
          inputProps={{ 'aria-label': t('input-label-search') }}
          onChange={(event) => setSearch(event.target.value)}
          // The menu reads keystrokes to jump between options; these ones are not for it.
          onKeyDown={(event) => {
            if (event.key !== 'Escape') event.stopPropagation();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </ListSubheader>
      {showSelectedSection && [
        <ListSubheader key="selected-header" sx={{ backgroundColor: 'background.paper' }}>
          {t('fleet-management-filter-selected-header')}
        </ListSubheader>,
        ...selectedMatches.map((option) => renderOption(option, 'selected')),
        <Divider key="selected-divider" />,
      ]}
      {renderedMenuItems}
      {hiddenMatchCount > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, py: 1 }}>
          {t('fleet-management-filter-more-options', { count: hiddenMatchCount })}
        </Typography>
      )}
      {matches.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
          {t('fleet-management-filter-no-matches')}
        </Typography>
      )}
    </SelectInput>
  );
};

export default React.memo(FilterMultiSelect);
